import React from "react";
import { BookmarkCheck, Eye, MoreHorizontal, FileDown, FileSpreadsheet, Trash2, Pencil } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";

const CollectionCard = ({
  collection,
  onView,
  onRename,
  onExportCSV,
  onExportExcel,
  onDelete,
}) => {
  const formattedDate = collection.createdAt
    ? new Date(collection.createdAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Recently saved";

  return (
    <Card className="border-gray-200 hover:border-gray-300 hover:shadow-xs transition-all duration-200 bg-white">
      <CardContent className="p-4 sm:p-5 flex items-center gap-4">
        {/* Left icon */}
        <div className="w-10 h-10 rounded-lg bg-explore/10 text-explore flex items-center justify-center flex-shrink-0 border border-explore/20">
          <BookmarkCheck size={20} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              onClick={onView}
              className="font-semibold text-gray-900 truncate text-sm hover:text-explore cursor-pointer"
              title={collection.name}
            >
              {collection.name}
            </span>
            <Badge
              variant="secondary"
              className="text-[10px] px-1.5 py-0 h-4 capitalize bg-gray-100 text-gray-700 font-medium"
            >
              {collection.entityType || "general"}
            </Badge>
            <Badge
              variant="outline"
              className="text-[10px] px-1.5 py-0 h-4 text-explore border-explore/20 bg-explore/5"
            >
              Top {collection.limit || 20}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 m-0">
            <span className="font-medium text-gray-700">{collection.items?.length || 0}</span> items • Saved {formattedDate}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1.5 text-gray-700 hover:text-explore hover:border-explore/40 border-gray-200"
            onClick={onView}
          >
            <Eye size={13} />
            View
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-400 hover:text-gray-900 rounded-md"
              >
                <MoreHorizontal size={15} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40 bg-white shadow-lg border-gray-200">
              {onRename && (
                <DropdownMenuItem
                  onClick={onRename}
                  className="text-xs cursor-pointer gap-2 py-2"
                >
                  <Pencil size={14} className="text-gray-500" />
                  Rename
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={onExportCSV}
                className="text-xs cursor-pointer gap-2 py-2"
              >
                <FileDown size={14} className="text-gray-500" />
                Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onExportExcel}
                className="text-xs cursor-pointer gap-2 py-2"
              >
                <FileSpreadsheet size={14} className="text-green-600" />
                Export Excel
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-xs cursor-pointer gap-2 py-2 text-rose-600 focus:text-rose-700 focus:bg-rose-50"
              >
                <Trash2 size={14} />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

export default CollectionCard;
